import { useMutationState, useQueryClient } from "@tanstack/react-query";
import { BookOpen, Rocket, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  isApiError,
  type ProjectSummary,
  queryKeys,
  REMOVE_PROJECT_MUTATION_KEY,
  useGroups,
  useMyGroups,
  useProjects,
  useRemoveProject,
} from "@/lib/api";
import { useUser } from "@/lib/user-context";

import { ProjectCard } from "./project-card";
import { ProjectDetailDialog } from "./project-detail-dialog";

// Sentinel group-filter value for ungrouped projects. Cannot
// collide with a real group id (ids are numeric).
const GROUP_UNASSIGNED = "__ungrouped__";

// Fields matched by the homescreen search. Covers the project itself plus its
// creator (owner name/email) so colleagues can find a project by more than its
// name -- e.g. who created it or what it's for.
function projectMatchesQuery(project: ProjectSummary, query: string): boolean {
  const haystack = [
    project.project_name,
    project.project_slug,
    project.description,
    project.owner_name,
    project.owner_email,
    project.status,
  ];
  return haystack.some((field) => field?.toLowerCase().includes(query));
}

interface ProjectListProps {
  githubConnected: boolean;
  refreshTrigger?: number;
  newProjectButton?: React.ReactNode;
  projectId?: number;
  tab?: string;
  openProject: (id: number, tab?: string) => void;
  closeProject: () => void;
  setProjectTab: (tab: string) => void;
}

export function ProjectList({
  githubConnected,
  refreshTrigger,
  newProjectButton,
  projectId,
  tab,
  openProject,
  closeProject,
  setProjectTab,
}: ProjectListProps) {
  const queryClient = useQueryClient();
  const { data: allProjects = [], isLoading } = useProjects(githubConnected);
  // Projects whose removal is still in flight stay hidden even when a
  // background refetch (deploy-URL poll, window focus, another mutation's
  // invalidation) returns them before the backend has finished tearing down.
  const pendingRemovalIds = new Set(
    useMutationState({
      filters: { mutationKey: REMOVE_PROJECT_MUTATION_KEY, status: "pending" },
      select: (mutation) => (mutation.state.variables as { projectId: number }).projectId,
    }),
  );
  const projects = allProjects.filter((p) => !pendingRemovalIds.has(p.id));
  // While GitHub isn't connected the query is disabled, so it never "loads".
  const loading = githubConnected && isLoading;
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");
  const trimmedSearch = search.trim().toLowerCase();

  const { user } = useUser();
  const isAdmin = user?.role === "admin";
  // Admins see every project (including groups they aren't a member of), so
  // resolving names needs the full admin group list; everyone else only ever
  // needs -- and only ever has access to -- their own memberships.
  const { data: allGroups = [] } = useGroups(isAdmin);
  const { data: myGroups = [] } = useMyGroups();
  const groups = isAdmin ? allGroups : myGroups;

  // Filter options = groups the viewer knows (membership / admin list) merged
  // with any group a *visible* project belongs to -- so a grouped project shared
  // explicitly with a non-member (via project_members) stays filterable.
  const groupOptions = useMemo(() => {
    const map = new Map<number, string>();
    for (const g of groups) {
      map.set(g.id, g.name);
    }
    for (const p of projects) {
      if (p.group_id != null && p.group_name) {
        map.set(p.group_id, p.group_name);
      }
    }
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [groups, projects]);

  const filteredProjects = useMemo(
    () =>
      projects
        .filter((project) => !trimmedSearch || projectMatchesQuery(project, trimmedSearch))
        .filter((project) => {
          if (groupFilter === "all") {
            return true;
          }
          if (groupFilter === GROUP_UNASSIGNED) {
            return project.group_id == null;
          }
          return String(project.group_id) === groupFilter;
        }),
    [projects, trimmedSearch, groupFilter],
  );

  // Runs the removal after the dialog has closed: the project leaves the list
  // right away and comes back only if the removal really failed.
  const removeProject = useRemoveProject();
  const handleRemoveProject = useCallback(
    (project: ProjectSummary) => {
      closeProject();
      toast.promise(removeProject.mutateAsync({ projectId: project.id }), {
        loading: `Removing ${project.project_name}...`,
        success: `${project.project_name} removed`,
        error: (err: unknown) => {
          if (isApiError(err)) {
            const detail = (err.body as { detail?: string } | undefined)?.detail;
            if (detail) {
              return detail;
            }
          }
          return err instanceof Error && err.message ? err.message : "Failed to remove project";
        },
      });
    },
    [closeProject, removeProject],
  );

  // Optimistically patch a project's resource URLs in the cache after a
  // resource is deleted, so the list reflects the change without a refetch.
  const handleResourceUpdated = useCallback(
    (pid: string | number, updates: Partial<ProjectSummary>) => {
      queryClient.setQueryData<ProjectSummary[]>(queryKeys.projects, (prev) =>
        prev ? prev.map((p) => (p.id === pid ? { ...p, ...updates } : p)) : prev,
      );
    },
    [queryClient],
  );

  const selectedProject = projects.find((p) => p.id === projectId) ?? null;
  const detailOpen = !!projectId && !!selectedProject;

  // Re-fetch when the parent bumps refreshTrigger (e.g. after project creation
  // or an out-of-sync resolution).
  useEffect(() => {
    if (githubConnected && refreshTrigger) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects });
    }
  }, [refreshTrigger, githubConnected, queryClient]);

  // Close dialog if projectId in URL doesn't match any loaded project
  useEffect(() => {
    if (!loading && projectId && projects.length > 0 && !projects.find((p) => p.id === projectId)) {
      closeProject();
    }
  }, [loading, projectId, projects, closeProject]);

  if (!githubConnected) {
    return (
      <p className="text-muted-foreground py-12 text-center text-sm">
        Connect your GitHub account above to start creating projects.
      </p>
    );
  }

  if (loading) {
    return <p className="text-muted-foreground py-12 text-center text-sm">Loading projects...</p>;
  }

  if (projects.length === 0) {
    return (
      <div className="flex flex-col items-center gap-4 py-16 text-center">
        <div className="bg-accent/10 rounded-full p-4">
          <Rocket className="text-accent size-8" />
        </div>
        <div>
          <p className="text-primary text-lg font-bold">No projects yet</p>
          <p className="text-muted-foreground mt-1 text-sm">
            Create your first project or learn how Norican works
          </p>
        </div>
        <div className="flex gap-3">
          {newProjectButton}
          <Link to="/how-to">
            <Button variant="outline" size="sm">
              <BookOpen className="mr-2 size-4" />
              How to Get Started
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mb-4 flex items-center gap-2">
        <div className="relative max-w-sm flex-1">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, description, creator..."
            aria-label="Search projects"
            className="pl-9"
          />
        </div>
        {groupOptions.length > 0 && (
          <Select value={groupFilter} onValueChange={setGroupFilter}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="All groups" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All groups</SelectItem>
              <SelectItem value={GROUP_UNASSIGNED}>Ungrouped</SelectItem>
              {groupOptions.map((g) => (
                <SelectItem key={g.id} value={String(g.id)}>
                  {g.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {filteredProjects.length === 0 ? (
        <p className="text-muted-foreground py-12 text-center text-sm">
          {trimmedSearch
            ? `No projects match “${search.trim()}”.`
            : "No projects match the selected group."}
        </p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {filteredProjects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onClick={() => openProject(project.id)}
              groupName={project.group_name}
            />
          ))}
        </div>
      )}

      <ProjectDetailDialog
        project={selectedProject}
        open={detailOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeProject();
          }
        }}
        onRemoveProject={handleRemoveProject}
        onResourceUpdated={handleResourceUpdated}
        tab={tab}
        onTabChange={setProjectTab}
      />
    </>
  );
}
